import PlayCircleOutlinedIcon from "@mui/icons-material/PlayCircleOutlined";
import { Grid } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useState } from "react";
import { TrainingService } from "../../api/TrainingService";
import useTranslation from "../../hooks/useTranslation";
import TrainingTouchpoint from "../../models/trainingTouchpoint";
import TouchpointCardPlaceholder from "../../styles/illustrations/TouchpointCardPlaceholder.png";
import { DeliveryMethod } from "../../types/training";
import { Button } from "../ui/Button/Button";
import { Card } from "../ui/Card/Card";
import { Modal } from "../ui/Modal/Modal";
import { Tooltip } from "../ui/Tooltip/Tooltip";
import { Typography } from "../ui/Typography/Typography";
import { IconTypes } from "../ui/icons";
import { Icon } from "../ui/icons/Icon";
import "./TouchpointCard.scss";

interface TouchpointCardProps {
  touchpoint: TrainingTouchpoint;
  order: number;
  canAccess: boolean;
  deliveryMethod: DeliveryMethod;
}

export const TouchpointCard = ({
  touchpoint,
  order,
  canAccess,
  deliveryMethod,
}: TouchpointCardProps) => {
  const { t } = useTranslation("trainingPage");
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const trainingService = new TrainingService();
  const [isCopied, setIsCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(touchpoint.directLink);
    setIsCopied(true);
  };

  const handleDownloadOrCopy = () => {
    if (deliveryMethod === DeliveryMethod.Link) {
      handleCopy();
    } else {
      refetch();
    }
  };

  const { error: downloadError, refetch } = useQuery<Blob, AxiosError>(
    ["trainingDownload"],
    () => trainingService.downloadTouchpoint(touchpoint.referenceId),
    {
      refetchOnWindowFocus: false,
      enabled: false,
      retry: false,
    }
  );

  const getIcon = () => {
    if (deliveryMethod === DeliveryMethod.Link) {
      return isCopied ? IconTypes.Check : IconTypes.Copy;
    }
    return IconTypes.Download;
  };

  const getTooltipTitle = (order: number) => {
    if (deliveryMethod === DeliveryMethod.Link) {
      return isCopied
        ? t("modules.delivery.copySuccess")
        : t("modules.delivery.copyTouchpoint", { order });
    }
    return t("modules.delivery.downloadTouchpoint", { order });
  };

  return (
    <div>
      <Card className="TouchpointCard">
        <Grid container columnSpacing={1}>
          <Grid item xs={12} sm={7} className="TouchpointCard__content">
            <div>
              <Typography
                tagVariant="h2"
                desktop="h3"
                className="TouchpointCard__content__title"
              >
                {touchpoint.title}
              </Typography>
              <Typography
                tagVariant="p"
                desktop="body2"
                className="TouchpointCard__content__description"
              >
                {touchpoint.description}
              </Typography>
            </div>
            <div className="TouchpointCard__content__footer">
              <div className="TouchpointCard__content__footer__download">
                {!canAccess && (
                  <Typography color="error" desktop="caption">
                    {t("modules.delivery.downloadDisabled")}
                  </Typography>
                )}
                <Tooltip title={getTooltipTitle(order)}>
                  <Button
                    className="TouchpointCard__content__footer__download__button"
                    onClick={() => handleDownloadOrCopy()}
                    variant="contained"
                    color="secondary"
                    small={true}
                    disabled={!canAccess}
                  >
                    <Icon type={getIcon()} color="secondary" size={24} />
                    {t("modules.delivery.touchpoint", { order })}
                  </Button>
                </Tooltip>
              </div>
            </div>
            {!!downloadError && (
              <Typography color="error" desktop="caption">
                {t("modules.delivery.downloadError")}
              </Typography>
            )}
          </Grid>
          <Grid item xs={12} sm={5} className="TouchpointCard__thumbnail">
            <div className="TouchpointCard__thumbnail__wrapper">
              {touchpoint.thumbnail ? (
                <img
                  className="TouchpointCard__thumbnail__wrapper__img"
                  src={touchpoint.thumbnail}
                  alt={t("modules.preview")}
                  onClick={() => setModalOpen(true)}
                />
              ) : (
                <img
                  className="TouchpointCard__thumbnail__wrapper__img"
                  src={TouchpointCardPlaceholder}
                  alt={t("modules.preview")}
                />
              )}
              <div className="TouchpointCard__thumbnail__wrapper__overlay">
                <Button
                  className="TouchpointCard__thumbnail__wrapper__overlay__text"
                  onClick={() => setModalOpen(true)}
                  variant="contained"
                  color="primary"
                  small={true}
                >
                  <PlayCircleOutlinedIcon />
                  {t("modules.preview")}
                </Button>
              </div>
            </div>
          </Grid>
        </Grid>
      </Card>
      <Modal
        className="TouchpointCard__preview__modal"
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={
          <Typography tagVariant="h3" desktop="h3" weight="bold">
            {touchpoint.title}
          </Typography>
        }
      >
        <iframe
          className="TouchpointCard__preview__modal__content__iframe"
          title={touchpoint.title}
          src={touchpoint.previewLink}
        ></iframe>
      </Modal>
    </div>
  );
};
